import styles from "./remove-member-modal.module.css";
import { useEffect, useMemo, useState } from "react";
import { Modal, Typography, Collapse } from "antd";

import { deleteMember, getEmployeeStatistics } from "@/api/units";

import CustomSelect from "@/components/inputs/CustomSelect";

const { Text } = Typography;

// Окно при удалении сотрудника из РГ

// Утилитарные функции
const formatEmployeeName = (employee) => {
  if (!employee) return "";
  return `${employee.lastname || ""} ${employee.firstname || ""} ${
    employee.patronymic || ""
  }`.trim();
};

const createMemberOption = (member) => ({
  ...member,
  label: formatEmployeeName(member),
});

// Подкомпонент для отображения секции ролей
const RoleSection = ({
  title,
  selectTitle,
  tasks,
  templates,
  selectedMember,
  onMemberChange,
  unitMembersOptions,
}) => {
  const hasItems = !!tasks.length || !!templates.length;

  const collapseItems = [
    {
      key: "tasks",
      label: `Задачи (${tasks.length})`,
      children: !tasks.length ? (
        <Text>Нет задач</Text>
      ) : (
        tasks.map((task) => (
          <div key={task.task_uuid}>
            №{task.task_number} {task.name}
          </div>
        ))
      ),
    },
    {
      key: "templates",
      label: `Шаблоны (${templates.length})`,
      children: !templates.length ? (
        <Text>Нет шаблонов</Text>
      ) : (
        templates.map((template) => (
          <div key={template.template_uuid}>{template.name}</div>
        ))
      ),
    },
  ];

  return (
    <div style={{ marginBottom: 20 }}>
      <div className={styles.roleTitle}>{title}</div>

      <Collapse items={collapseItems} />

      {hasItems && (
        <>
          <div style={{ marginTop: 10 }}>Выберите нового {selectTitle}:</div>
          <CustomSelect
            placeholder="Начните вводить"
            options={unitMembersOptions}
            showSearch
            allowClear
            fieldNames={{ value: "uuid" }}
            optionFilterProp="label"
            value={selectedMember}
            onChange={onMemberChange}
            className={styles.select}
          />
        </>
      )}
    </div>
  );
};

// Основной компонент
const RemoveMemberModal = ({
  unit,
  employee,
  visible,
  onConfirm,
  onCancel,
}) => {
  // Состояние
  const [statistics, setStatistics] = useState({
    responsible: { tasks: [], templates: [] },
    acceptor: { tasks: [], templates: [] },
  });

  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState(null);

  const [selectNewResponsible, setSelectNewResponsible] = useState(null);
  const [selectNewAcceptor, setSelectNewAcceptor] = useState(null);

  // Мемоизированные значения
  const unitMembersOptions = useMemo(() => {
    if (!unit?.members) return [];

    return unit.members
      .filter((member) => member.uuid !== employee?.uuid)
      .map(createMemberOption);
  }, [unit?.members, employee?.uuid]);

  const employeeName = useMemo(() => formatEmployeeName(employee), [employee]);

  // Эффекты
  useEffect(() => {
    if (visible) {
      fetchEmployeeStatistics();
    }
    // eslint-disable-next-line
  }, [visible]);

  const fetchEmployeeStatistics = async () => {
    if (!employee?.uuid) return;

    setIsLoading(true);
    try {
      const stats = await getEmployeeStatistics({
        employee_uuid: employee.uuid,
        unit_uuid: unit.uuid,
      });

      setStatistics({
        responsible: {
          tasks: stats.responsible?.tasks?.data || [],
          templates: stats.responsible?.templates?.data || [],
        },
        acceptor: {
          tasks: stats.acceptor?.tasks?.data || [],
          templates: stats.acceptor?.templates?.data || [],
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = async () => {
    if (!unit?.uuid || !employee?.uuid) return;

    try {
      setIsLoading(true);

      await deleteMember({
        unit_uuid: unit.uuid,
        employee_uuid: employee.uuid,
        responsible_executor_uuid: selectNewResponsible,
        assignee_acceptor_uuid: selectNewAcceptor,
      });

      onConfirm();
    } catch (error) {
      setError(error?.message || "Ошибка при удалении участника");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      maskClosable={false}
      title="Исключение сотрудника из рабочей группы"
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
      okText="Подтвердить"
      cancelText="Отменить"
      okButtonProps={{ loading: isLoading }}
      width={520}
    >
      <Text className={styles.warningText}>
        Внимание! Вы исключаете сотрудника <b>{employeeName}</b> из&nbsp;рабочей
        группы {unit.name}. Перед исключением необходимо переназначить его
        активные задачи и&nbsp;шаблоны.
      </Text>

      <Text className={styles.instructionText}>
        Ниже представлены задачи и&nbsp;шаблоны, где&nbsp;данный сотрудник
        является участником. Необходимо выбрать замещающего участника
        из&nbsp;состава рабочей группы.
      </Text>

      <RoleSection
        title="Исполнитель"
        selectTitle="исполнителя"
        tasks={statistics.responsible.tasks}
        templates={statistics.responsible.templates}
        selectedMember={selectNewResponsible}
        onMemberChange={setSelectNewResponsible}
        unitMembersOptions={unitMembersOptions}
      />

      <RoleSection
        title="Приемщик"
        selectTitle="приемщика"
        tasks={statistics.acceptor.tasks}
        templates={statistics.acceptor.templates}
        selectedMember={selectNewAcceptor}
        onMemberChange={setSelectNewAcceptor}
        unitMembersOptions={unitMembersOptions}
      />

      {error && <Text type="danger">{error}</Text>}
    </Modal>
  );
};

export default RemoveMemberModal;
